/**
 * The FIRST TOUCH: where this browser came in, kept until the purchase.
 *
 * wiswiz #287 / #290. The payment row keeps the Meta click id (`_fbc`), but
 * the checkout page URL has lost the utm of the landing, so today the ad
 * behind a sale is only recoverable through a PostHog join, and a student
 * who signs up carries no origin at all. This cookie is written ONCE, on the
 * server, at the first page request of a browser (middleware.ts), and it is
 * never overwritten: the first touch is the touch that started the customer.
 * The browser reads it back when a checkout or a signup starts and sends it
 * in the request body, exactly like the click ids in metaClick.ts, and the
 * backend persists it on the payment row and on the user.
 *
 * One cookie, one shape, both sides:
 *   _wz_touch = v=1&t=<ms>&l=<landing path+query>&r=<referrer host>
 *               &s=<utm_source>&m=<utm_medium>&c=<utm_campaign>
 *               &n=<utm_content>&k=<utm_term>&f=<fbclid>
 * Keys are URL-encoded (application/x-www-form-urlencoded); absent parts are
 * left out. The landing keeps its query so utm_* and fbclid survive, minus the
 * secret-carrying keys we never store. Everything is bounded so the cookie
 * always fits and a hostile query cannot grow it.
 *
 * Not httpOnly: the page must read it to send it. 90 days, like `_fbc`.
 */

export const FIRST_TOUCH_COOKIE = "_wz_touch";
export const FIRST_TOUCH_VERSION = "1";
/* Meta's own window for a click id is 90 days; the first touch keeps pace. */
export const FIRST_TOUCH_MAX_AGE_SEC = 90 * 24 * 60 * 60;

const LANDING_MAX = 512;
const REFERRER_MAX = 128;
const UTM_MAX = 200;
const FBCLID_MAX = 400;
/* Well under the 4096-byte cookie limit, with room for the other cookies
   (session token, PostHog, _fbc). Measured on the encoded value. */
const COOKIE_MAX = 1200;

/* Query keys that carry a secret or a person: never in the landing we keep.
   The same list lives in the backend (app/services/first_touch.py). */
export const LANDING_SECRET_KEYS = new Set([
  "sharecode",
  "referral_receipt",
  "token",
  "parent_key",
  "code",
  "state",
  "registration_token",
  "email",
  "session",
  "id_token",
  "access_token",
  "gift",
  "gift_token",
  "share",
  "invite",
  "invite_token"
]);

/* Routes whose next path segment is a token (claim, invite, photo session):
   the segment is replaced, the route prefix stays. Same list in the backend. */
const TOKEN_ROUTES = /^(\/invite\/gift|\/invite|\/voor-je-kind|\/foto)\/[^/?#]+/;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term"
] as const;
type UtmKey = (typeof UTM_KEYS)[number];

const SHORT: Record<UtmKey, string> = {
  utm_source: "s",
  utm_medium: "m",
  utm_campaign: "c",
  utm_content: "n",
  utm_term: "k"
};

export type FirstTouch = {
  /** epoch milliseconds of the first page request */
  at: number;
  /** path + query of the first page, secrets removed */
  landing: string;
  /** host of the referrer, empty when none or our own site */
  referrer: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
};

const FBCLID_VORM = /^[A-Za-z0-9_-]{1,400}$/;

function knip(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** The landing path + query, without the keys that carry a secret. */
export function schoonLanding(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  params.forEach((value, key) => {
    if (LANDING_SECRET_KEYS.has(key.toLowerCase())) return;
    kept.append(key, value);
  });
  const q = kept.toString();
  const path = pathname.replace(TOKEN_ROUTES, "$1/_");
  return knip(path + (q ? `?${q}` : ""), LANDING_MAX);
}

/** The referrer as a host only; empty for none, or for our own site. */
export function referrerHost(referrer: string | null | undefined, ownHost: string): string {
  if (!referrer) return "";
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return "";
  }
  if (!host) return "";
  const own = ownHost.toLowerCase().replace(/:\d+$/, "");
  const ownRoot = own.replace(/^www\./, "");
  if (host === own || host === ownRoot || host.endsWith(`.${ownRoot}`)) return "";
  return knip(host, REFERRER_MAX);
}

/**
 * Build the cookie value for this arrival. Pure: the middleware hands in the
 * request parts, a test hands in fixed ones.
 */
export function bouwEersteAanraking(input: {
  pathname: string;
  search: string;
  referrer: string | null | undefined;
  host: string;
  nu?: number;
}): string {
  const params = new URLSearchParams(input.search);
  const out = new URLSearchParams();
  out.set("v", FIRST_TOUCH_VERSION);
  out.set("t", String(input.nu ?? Date.now()));
  out.set("l", schoonLanding(input.pathname, input.search));
  const ref = referrerHost(input.referrer, input.host);
  if (ref) out.set("r", ref);
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) out.set(SHORT[key], knip(value, UTM_MAX));
  }
  const fbclid = params.get("fbclid");
  if (fbclid && FBCLID_VORM.test(fbclid)) out.set("f", knip(fbclid, FBCLID_MAX));
  let value = out.toString();
  /* Measured as it goes on the wire: the cookie jar percent-encodes the value. */
  if (encodeURIComponent(value).length > COOKIE_MAX) {
    /* Too long: the landing query is the only unbounded part. Keep the path. */
    out.set("l", knip(input.pathname, LANDING_MAX));
    value = out.toString();
  }
  return value;
}

/** Parse a cookie value back; null when it is not ours. */
export function parseEersteAanraking(value: string | null | undefined): FirstTouch | null {
  if (!value) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(value);
  } catch {
    return null;
  }
  if (params.get("v") !== FIRST_TOUCH_VERSION) return null;
  const at = Number(params.get("t"));
  const landing = params.get("l") ?? "";
  if (!Number.isFinite(at) || at <= 0 || !landing.startsWith("/")) return null;
  const touch: FirstTouch = {
    at: Math.floor(at),
    landing: knip(landing, LANDING_MAX),
    referrer: knip(params.get("r") ?? "", REFERRER_MAX)
  };
  for (const key of UTM_KEYS) {
    const v = params.get(SHORT[key]);
    if (v) touch[key] = knip(v, UTM_MAX);
  }
  const f = params.get("f");
  if (f && FBCLID_VORM.test(f)) touch.fbclid = f;
  return touch;
}

/** Read the cookie in the browser; null when absent or unreadable. */
export function leesEersteAanraking(): FirstTouch | null {
  if (typeof document === "undefined") return null;
  try {
    const deel = document.cookie
      .split("; ")
      .find((c) => c.startsWith(FIRST_TOUCH_COOKIE + "="));
    if (!deel) return null;
    const raw = deel.slice(FIRST_TOUCH_COOKIE.length + 1);
    return parseEersteAanraking(raw ? decodeURIComponent(raw) : null);
  } catch {
    /* an iframe without access, storage blocked, a broken percent sign:
       measuring must never block a checkout, so this is 'no first touch' */
    return null;
  }
}

/**
 * Is this request the arrival of a PAGE, the only kind that has a landing?
 * Static files, the API proxy, PostHog ingest and Next's own prefetches are
 * not arrivals: a prefetch of /voor-ouders from the homepage would otherwise
 * record /voor-ouders as the landing of a visitor who came in on /.
 */
export function isPaginaAankomst(input: {
  method: string;
  pathname: string;
  headers: { get(name: string): string | null };
}): boolean {
  if (input.method !== "GET") return false;
  const p = input.pathname;
  if (
    p.startsWith("/api/") ||
    p.startsWith("/_next/") ||
    p.startsWith("/ingest/") ||
    p === "/favicon.ico"
  ) {
    return false;
  }
  const last = p.slice(p.lastIndexOf("/") + 1);
  if (last.includes(".")) return false;
  if (input.headers.get("rsc") || input.headers.get("next-router-prefetch")) return false;
  const dest = input.headers.get("sec-fetch-dest");
  if (dest && dest !== "document") return false;
  return true;
}
